import { CheckCircle2, ExternalLink, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { PlanInitialInput, PlanType } from '@/lib/types';

export interface PlanInitialRevision {
  plan_type: PlanType;
  id: number;
  title: string;
  version: number;
  url?: string;
  initialled_at?: string | null;
  initials?: string;
}

interface PlanInitialsFieldsProps {
  plans: PlanInitialRevision[];
  values: PlanInitialInput[];
  onChange: (values: PlanInitialInput[]) => void;
  disabled?: boolean;
  timeZone?: string;
}

export function PlanInitialsFields({
  plans,
  values,
  onChange,
  disabled = false,
  timeZone = 'UTC',
}: PlanInitialsFieldsProps) {
  if (plans.length === 0) return null;

  const updateInitials = (plan: PlanInitialRevision, initials: string) => {
    const next = values.filter(
      (item) =>
        item.plan_type !== plan.plan_type || item.plan_revision_id !== plan.id,
    );
    next.push({
      plan_type: plan.plan_type,
      plan_revision_id: plan.id,
      // Mirrors the server rule: letters plus spaces, periods, hyphens, apostrophes.
      initials: initials.replace(/[^\p{L}\p{M} .'-]/gu, '').toUpperCase(),
    });
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <ShieldCheck className="h-4 w-4 text-amber-600" />
        Initial the plans after your briefing
      </div>
      {plans.map((plan) => {
        const value =
          values.find(
            (item) =>
              item.plan_type === plan.plan_type &&
              item.plan_revision_id === plan.id,
          )?.initials ?? '';
        const label =
          plan.plan_type === 'safe_work_plan'
            ? 'Safe Work Plan'
            : 'Rescue Plan';
        return (
          <div
            key={`${plan.plan_type}-${plan.id}`}
            className="grid gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3 sm:grid-cols-[1fr_8rem]"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {label}: {plan.title}
              </p>
              <p className="text-xs text-muted-foreground">
                Revision {plan.version}
              </p>
              {plan.url && (
                <Link
                  to={plan.url}
                  target="_blank"
                  className="mt-1 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                >
                  Open plan <ExternalLink className="h-3 w-3" />
                </Link>
              )}
            </div>
            {plan.initialled_at ? (
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>
                  Initialled{plan.initials ? ` (${plan.initials})` : ''} rev{' '}
                  {plan.version} on{' '}
                  {formatDateTimeInTimeZone(plan.initialled_at, timeZone, {
                    dateStyle: 'medium',
                  })}
                </span>
              </div>
            ) : (
              <div>
                <Label
                  htmlFor={`plan-initials-${plan.plan_type}-${plan.id}`}
                  className="text-xs"
                >
                  Your initials
                </Label>
                <Input
                  id={`plan-initials-${plan.plan_type}-${plan.id}`}
                  value={value}
                  onChange={(event) =>
                    updateInitials(plan, event.target.value.slice(0, 20))
                  }
                  placeholder="Optional"
                  autoComplete="off"
                  disabled={disabled}
                  className="mt-1 uppercase"
                />
              </div>
            )}
          </div>
        );
      })}
      <p className="text-xs text-muted-foreground">
        Initials are optional. You can continue if a briefing is still pending.
      </p>
    </div>
  );
}
